"use client";

import type { Dispatch } from 'react';
import {
  useEffect,
  useRef,
  useState,
} from 'react';

import Script from 'next/script';

type TurnstileStatus = 'ready' | 'verified' | 'expired' | 'error' | 'timeout' | 'unsupported';
type TurnstileStatusPayload = [TurnstileStatus, string?];

interface CloudflareTurnstileProps {
  siteKey: string;
  onTokenChange: Dispatch<string | null>;
  onStatusChange?: Dispatch<TurnstileStatusPayload>;
  resetKey?: number;
  theme?: 'light' | 'dark' | 'auto';
  className?: string;
}

export default function CloudflareTurnstile({
  siteKey,
  onTokenChange,
  onStatusChange,
  resetKey = 0,
  theme = 'auto',
  className = '',
}: CloudflareTurnstileProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [scriptReady, setScriptReady] = useState<boolean>(typeof window !== 'undefined' && !!window.turnstile);

  useEffect(() => {
    if (!scriptReady || !siteKey || !containerRef.current || !window.turnstile) {
      return;
    }

    if (widgetIdRef.current) {
      window.turnstile.remove(widgetIdRef.current);
      widgetIdRef.current = null;
    }

    containerRef.current.innerHTML = '';
    onTokenChange(null);
    onStatusChange?.(['ready']);

    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: siteKey,
      theme,
      callback: (token: string) => {
        onTokenChange(token);
        onStatusChange?.(['verified']);
      },
      'expired-callback': () => {
        onTokenChange(null);
        onStatusChange?.(['expired']);
      },
      'error-callback': (errorCode?: string) => {
        onTokenChange(null);
        onStatusChange?.(['error', errorCode]);
      },
      'timeout-callback': () => {
        onTokenChange(null);
        onStatusChange?.(['timeout']);
      },
      'unsupported-callback': () => {
        onTokenChange(null);
        onStatusChange?.(['unsupported']);
      },
    });

    return () => {
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [onStatusChange, onTokenChange, resetKey, scriptReady, siteKey, theme]);

  return (
    <>
      <Script
        id="cloudflare-turnstile-script"
        src="https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit"
        strategy="afterInteractive"
        onLoad={() => setScriptReady(true)}
      />
      <div
        ref={containerRef}
        className={className}
      />
    </>
  );
}
